import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Toast } from 'primereact/toast';
import {
  getAuthUser, roleLabel, canManageCarrierUsers,
} from '../../services/authApi';
import {
  fetchCarrierUsers, createCarrierUser, updateCarrierUser, scopeLabel,
  CarrierUser, CarrierUserInput,
} from '../../services/carrierApi';
import { getAvatarColor, getInitials } from '../../components/dashboard/DashboardKit';

/* Carrier user management — Carrier Admin only.
   Scoped to the admin's own CarrierId: they can never see or touch
   another carrier's team. */

const EMPTY_FORM: CarrierUserInput = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  phoneNumber: '',
};

/** Matches the API's [StringLength(128, MinimumLength = 8)] on Password. */
const MIN_PASSWORD = 8;

const EMAIL_RE = /^\S+@\S+\.\S+$/;

const CarrierUsers: React.FC = () => {
  const toast = useRef<Toast>(null);
  const user = getAuthUser();
  const allowed = canManageCarrierUsers(user);

  const [users, setUsers] = useState<CarrierUser[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<CarrierUserInput>(EMPTY_FORM);
  const [formError, setFormError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  useEffect(() => {
    let cancelled = false;
    fetchCarrierUsers()
      .then(res => {
        if (cancelled) return;
        setUsers(res.data);
      })
      .catch(() => !cancelled && setUsers([]))
      .finally(() => !cancelled && setIsLoading(false));
    return () => { cancelled = true; };
  }, []);

  const notify = (severity: 'success' | 'info' | 'warn', detail: string) =>
    toast.current?.show({ severity, summary: severity === 'success' ? 'Done' : 'Notice', detail, life: 2600 });

  const openAdd = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormError(null);
    setShowPassword(false);
    setModalOpen(true);
  };

  const openEdit = (u: CarrierUser) => {
    setEditingId(u.id);
    setForm({
      firstName: u.firstName,
      lastName: u.lastName,
      email: u.email,
      password: '',
      phoneNumber: u.phoneNumber,
    });
    setFormError(null);
    setShowPassword(false);
    setModalOpen(true);
  };

  const submitForm = async (e: React.FormEvent) => {
    e.preventDefault();

    // Mirrors the API's own validation so a bad value never costs a round trip.
    if (!form.firstName.trim()) return setFormError('Enter a first name.');
    if (!EMAIL_RE.test(form.email.trim())) return setFormError('Enter a valid email address.');
    if (!editingId && form.password.length < MIN_PASSWORD) {
      return setFormError(`Password must be at least ${MIN_PASSWORD} characters.`);
    }
    if (editingId && form.password && form.password.length < MIN_PASSWORD) {
      return setFormError(`Password must be at least ${MIN_PASSWORD} characters, or left blank.`);
    }

    const fullName = [form.firstName, form.lastName].filter(Boolean).join(' ').trim();
    setIsSaving(true);
    setFormError(null);

    try {
      if (editingId) {
        await updateCarrierUser(editingId, form);
        setUsers(prev =>
          prev.map(u =>
            u.id === editingId
              ? { ...u, firstName: form.firstName.trim(), lastName: form.lastName.trim(),
                  fullName, email: form.email.trim(), phoneNumber: form.phoneNumber.trim() }
              : u
          )
        );
        notify('success', `${fullName} updated.`);
      } else {
        const parentUserId = Number(user?.userId);
        if (!parentUserId) {
          setFormError('Your account has no user id — sign out and back in.');
          setIsSaving(false);
          return;
        }
        const created = await createCarrierUser(parentUserId, form);
        setUsers(prev => [{ ...created, carrierName: user?.carrierName || '' }, ...prev]);
        notify('success', `${fullName} can now sign in with ${created.email}.`);
      }
      setModalOpen(false);
    } catch (err: any) {
      setFormError(err?.message || 'Could not save the user. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  if (!allowed) {
    return (
      <div className="dashboard">
        <div className="denied-card">
          <div className="denied-card__icon">
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
          </div>
          <h2>User management is Carrier Admin only</h2>
          <p>
            You are signed in as {roleLabel(user?.roleName || 'CARRIER_AGENT')}. Ask your carrier
            administrator to change a team member’s access.
          </p>
        </div>
      </div>
    );
  }

  const filtered = users.filter(u =>
    `${u.fullName} ${u.email}`.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="dashboard">
      <Toast ref={toast} />

      <div className="um-summary">
        <div className="um-summary__item">
          <span className="um-summary__value">{users.length}</span>
          <span className="um-summary__label">Team members</span>
        </div>
        <div className="um-summary__item">
          <span className="um-summary__value">{users.filter(u => u.isActive).length}</span>
          <span className="um-summary__label">Active</span>
        </div>
        <div className="um-summary__item">
          <span className="um-summary__value">{users.filter(u => !u.isActive).length}</span>
          <span className="um-summary__label">Deactivated</span>
        </div>
        <div className="um-summary__scope">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
          {scopeLabel(user)}
        </div>
      </div>

      <div className="db-table-card">
        <div className="db-table-card__header">
          <div>
            <h2 className="db-table-card__title">Carrier Users</h2>
            <p className="db-table-card__sub">Add, edit and control access for your team</p>
          </div>
          <div className="db-table-card__actions">
            <div className="search-box search-box--sm">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search name or email…"
                value={search}
                onChange={e => setSearch(e.target.value)}
              />
            </div>
            <button className="btn-primary btn-primary--sm" onClick={openAdd}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="12" y1="5" x2="12" y2="19" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
              Add User
            </button>
          </div>
        </div>

        <div className="db-table-wrap">
          {filtered.length === 0 ? (
            <div className="db-empty">
              <svg width="30" height="30" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
              </svg>
              <p>{isLoading ? 'Loading your team…' : 'No users match that search.'}</p>
            </div>
          ) : (
            <table className="db-table um-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Phone</th>
                  <th>Added</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => (
                  <tr key={u.id} className={`db-table__row ${u.isActive ? '' : 'db-table__row--muted'}`}>
                    <td>
                      <div className="company-cell">
                        <span
                          className="company-cell__avatar"
                          style={{ background: getAvatarColor(u.fullName) }}
                        >
                          {getInitials(u.fullName)}
                        </span>
                        <div className="company-cell__info">
                          <span className="company-cell__name">{u.fullName}</span>
                          <span className="company-cell__meta">{u.email}</span>
                        </div>
                      </div>
                    </td>
                    <td><span className="db-date">{u.phoneNumber || '—'}</span></td>
                    <td><span className="db-date">{u.createdDate || '—'}</span></td>
                    <td>
                      <span className={`badge ${u.isActive ? 'badge--approved' : 'badge--rejected'}`}>
                        {u.isActive ? 'Active' : 'Deactivated'}
                      </span>
                    </td>
                    <td>
                      <div className="um-actions">
                        <button className="icon-btn" title="Edit user" onClick={() => openEdit(u)}>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                            <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
                          </svg>
                        </button>
                        <button
                          className="icon-btn"
                          title={`${u.isActive ? 'Deactivate' : 'Activate'} — coming soon`}
                          disabled
                        >
                          {u.isActive ? (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="15" y1="9" x2="9" y2="15" />
                              <line x1="9" y1="9" x2="15" y2="15" />
                            </svg>
                          ) : (
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                              <polyline points="22 4 12 14.01 9 11.01" />
                            </svg>
                          )}
                        </button>
                        <button className="icon-btn" title="Reset password — coming soon" disabled>
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 12a9 9 0 1 0 3-6.7L3 8" />
                            <polyline points="3 3 3 8 8 8" />
                          </svg>
                        </button>
                        <button
                          className="icon-btn"
                          title="Delete user — coming soon"
                          disabled
                        >
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                          </svg>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="db-table-footer">
          <span className="db-table-footer__info">
            Showing <strong>{filtered.length}</strong> of <strong>{users.length}</strong> team members
          </span>
        </div>
      </div>

      {/* ── Add User modal ──
          Portalled to <body>: MainLayout animates <main> with a transform, and a
          transformed ancestor becomes the containing block for position:fixed,
          which shifted the overlay as that animation settled. */}
      {modalOpen && createPortal(
        <div
          className="modal-overlay"
          role="presentation"
          onMouseDown={e => { if (e.target === e.currentTarget) setModalOpen(false); }}
        >
          <div className="modal-card" role="dialog" aria-modal="true" aria-label="Add User">
              <div className="modal-card__header">
                <h3>{editingId ? 'Edit User' : 'Add User'}</h3>
                <button className="icon-btn" onClick={() => setModalOpen(false)} aria-label="Close">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>

              <form className="modal-form" onSubmit={submitForm} noValidate>
                <div className="form-row">
                  <div className="form-field">
                    <label htmlFor="um-first">First name</label>
                    <input
                      id="um-first"
                      type="text"
                      value={form.firstName}
                      placeholder="Dana"
                      autoComplete="given-name"
                      onChange={e => setForm({ ...form, firstName: e.target.value })}
                    />
                  </div>
                  <div className="form-field">
                    <label htmlFor="um-last">Last name</label>
                    <input
                      id="um-last"
                      type="text"
                      value={form.lastName}
                      placeholder="Whitfield"
                      autoComplete="family-name"
                      onChange={e => setForm({ ...form, lastName: e.target.value })}
                    />
                  </div>
                </div>

                <div className="form-field">
                  <label htmlFor="um-email">Email</label>
                  <input
                    id="um-email"
                    type="email"
                    value={form.email}
                    placeholder="user@carrier.com"
                    autoComplete="off"
                    onChange={e => setForm({ ...form, email: e.target.value })}
                  />
                  <span className="form-hint">They sign in with this address.</span>
                </div>

                {(
                  <div className="form-field">
                    <label htmlFor="um-password">
                      {editingId ? 'New password (optional)' : 'Password'}
                    </label>
                    <div className="form-input-wrap">
                      <input
                        id="um-password"
                        type={showPassword ? 'text' : 'password'}
                        value={form.password}
                        placeholder={editingId ? 'Leave blank to keep current' : `At least ${MIN_PASSWORD} characters`}
                        autoComplete="new-password"
                        onChange={e => setForm({ ...form, password: e.target.value })}
                      />
                      <button
                        type="button"
                        className="form-eye-btn"
                        onClick={() => setShowPassword(v => !v)}
                        aria-label={showPassword ? 'Hide password' : 'Show password'}
                      >
                        {showPassword ? (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                            <path d="M1 1l22 22M6.61 6.61A18.15 18.15 0 0 0 1 12s4 8 11 8a9.12 9.12 0 0 0 5.39-1.61" />
                          </svg>
                        ) : (
                          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                            <circle cx="12" cy="12" r="3" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                )}

                <div className="form-field">
                  <label htmlFor="um-phone">Phone number</label>
                  <input
                    id="um-phone"
                    type="tel"
                    value={form.phoneNumber}
                    placeholder="Optional"
                    autoComplete="tel"
                    onChange={e => setForm({ ...form, phoneNumber: e.target.value })}
                  />
                </div>

                {formError && (
                  <div className="form-error" role="alert">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="12" cy="12" r="10" />
                      <line x1="12" y1="8" x2="12" y2="12" />
                      <line x1="12" y1="16" x2="12.01" y2="16" />
                    </svg>
                    {formError}
                  </div>
                )}

                <div className="modal-card__footer">
                  <button type="button" className="btn-ghost" onClick={() => setModalOpen(false)}>
                    Cancel
                  </button>
                  <button type="submit" className="btn-primary" disabled={isSaving}>
                    {isSaving ? 'Saving…' : editingId ? 'Save changes' : 'Add user'}
                  </button>
                </div>
              </form>
          </div>
        </div>,
        document.body
      )}

    </div>
  );
};

export default CarrierUsers;
